import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, X } from 'lucide-react';
import {
  GRID_COLS,
  GRID_ROWS,
  WIDGET_IDS,
  validateNoOverlap,
  type WidgetId,
  type WidgetLayout,
} from '@proadv/types';

import { useAuthStore } from '@/lib/auth-store';
import { apiClient } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { AddWidgetMenu } from '@/components/dashboard/add-widget-menu';
import { DashboardGrid } from '@/components/dashboard/dashboard-grid';
import { usePermissions } from '@/lib/use-permissions';
import type { AppModule } from '@/lib/use-permissions';

type Period = 'today' | 'week' | 'month' | 'year';
type Scope = 'personal' | 'team';

const PERIODS: { value: Period; label: string }[] = [
  { value: 'today', label: 'Hoje' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mês' },
  { value: 'year', label: 'Ano' },
];

const SCOPES: { value: Scope; label: string }[] = [
  { value: 'personal', label: 'Pessoal' },
  { value: 'team', label: 'Time' },
];

/**
 * Arranjo padrao: 8 widgets, 4 colunas x 2 linhas, cada um 1x1.
 * Espelha a ordem do dashboard estatico anterior.
 */
const DEFAULT_LAYOUT: WidgetLayout[] = [
  { widgetId: 'clients', x: 0, y: 0, w: 1, h: 1 },
  { widgetId: 'processes', x: 1, y: 0, w: 1, h: 1 },
  { widgetId: 'recent_clients', x: 2, y: 0, w: 1, h: 1 },
  { widgetId: 'upcoming_deadlines', x: 3, y: 0, w: 1, h: 1 },
  { widgetId: 'financial', x: 0, y: 1, w: 1, h: 1 },
  { widgetId: 'pending_tasks', x: 1, y: 1, w: 1, h: 1 },
  { widgetId: 'today_agenda', x: 2, y: 1, w: 1, h: 1 },
  { widgetId: 'new_intimations', x: 3, y: 1, w: 1, h: 1 },
];

function getGreeting(date: Date, name: string | null | undefined): string {
  const hour = date.getHours();
  const prefix =
    hour < 12 ? 'Bom dia' : hour < 18 ? 'Boa tarde' : 'Boa noite';
  const firstName = (name ?? '').trim().split(/\s+/)[0];
  return firstName ? `${prefix}, ${firstName}` : prefix;
}

/**
 * Saneia o layout vindo do backend:
 *  - descarta widgetIds nao reconhecidos no catalogo atual (compat futura)
 *  - clampa w/h dentro de [1..max]
 *  - se ficar vazio, retorna o DEFAULT
 */
function sanitizeLayout(input: WidgetLayout[] | null | undefined): WidgetLayout[] {
  if (!input || input.length === 0) return DEFAULT_LAYOUT;
  const known = new Set<string>(WIDGET_IDS);
  const out = input
    .filter((w) => known.has(w.widgetId))
    .map((w) => ({
      widgetId: w.widgetId,
      x: Math.max(0, Math.min(GRID_COLS - 1, Math.floor(w.x))),
      y: Math.max(0, Math.min(GRID_ROWS - 1, Math.floor(w.y))),
      w: Math.max(1, Math.min(4, Math.floor(w.w))),
      h: Math.max(1, Math.min(3, Math.floor(w.h))),
    }));
  return out.length > 0 ? out : DEFAULT_LAYOUT;
}

/**
 * Acha primeira posicao livre 1x1 para um novo widget. Varre y depois x.
 */
function findFreeSpot(layout: WidgetLayout[]): { x: number; y: number } {
  for (let y = 0; y < GRID_ROWS; y++) {
    for (let x = 0; x < GRID_COLS; x++) {
      const tentative: WidgetLayout = {
        widgetId: '__probe__' as WidgetId,
        x,
        y,
        w: 1,
        h: 1,
      };
      const conflict = validateNoOverlap([...layout, tentative]);
      if (!conflict) return { x, y };
    }
  }
  // Grid cheio (8 widgets em 4x3 nao acontece, mas defensivo).
  return { x: 0, y: GRID_ROWS - 1 };
}

/**
 * Mapeamento widget home -> modulo. Widgets sem entrada sao sempre visiveis.
 */
const HOME_WIDGET_MODULE: Partial<Record<string, AppModule>> = {
  clients: 'clients',
  recent_clients: 'clients',
  processes: 'cases',
  upcoming_deadlines: 'cases',
  financial: 'financial',
  pending_tasks: 'agenda',
  today_agenda: 'agenda',
  agenda_month: 'agenda',
  new_intimations: 'publications',
};

function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
  ariaLabel,
}: {
  items: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
  ariaLabel: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex items-center rounded-md border p-0.5',
        'border-[var(--border)] bg-[var(--surface-muted)]',
      )}
    >
      {items.map((item) => {
        const active = item.value === value;
        return (
          <button
            key={item.value}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(item.value)}
            className={cn(
              'rounded px-3 py-1 text-xs font-medium transition',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
              active
                ? 'bg-[var(--surface)] text-[var(--fg)] shadow-sm'
                : 'text-[var(--fg-muted)] hover:text-[var(--fg)]',
            )}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export default function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const { hasModule } = usePermissions();
  const [period, setPeriod] = useState<Period>('today');
  const [scope, setScope] = useState<Scope>('personal');
  const [mode, setMode] = useState<'view' | 'edit'>('view');
  const [draftLayout, setDraftLayout] = useState<WidgetLayout[] | null>(null);

  const queryClient = useQueryClient();
  const layoutQuery = useQuery({
    queryKey: ['me', 'dashboard-layout', 'home'],
    queryFn: () => apiClient.meGetDashboardLayout('home'),
    staleTime: 1000 * 60,
  });

  const savedLayout = useMemo(
    () => sanitizeLayout(layoutQuery.data?.layout),
    [layoutQuery.data],
  );

  const baseLayout = mode === 'edit' && draftLayout ? draftLayout : savedLayout;

  // Filtra widgets para member: remove os de modulos nao permitidos.
  const activeLayout = baseLayout.filter((w) => {
    const mod = HOME_WIDGET_MODULE[w.widgetId];
    if (!mod) return true; // sem restricao de modulo, sempre exibe
    return hasModule(mod);
  });

  const saveMutation = useMutation({
    mutationFn: (layout: WidgetLayout[]) =>
      apiClient.meUpdateDashboardLayout('home', layout),
    onSuccess: (res) => {
      queryClient.setQueryData(['me', 'dashboard-layout', 'home'], {
        layout: res.layout,
      });
      setMode('view');
      setDraftLayout(null);
    },
  });

  function enterEditMode() {
    setDraftLayout(savedLayout.map((w) => ({ ...w })));
    setMode('edit');
  }

  function cancelEdit() {
    setMode('view');
    setDraftLayout(null);
    saveMutation.reset();
  }

  function saveEdit() {
    if (!draftLayout) return;
    const overlap = validateNoOverlap(draftLayout);
    if (overlap) return;
    saveMutation.mutate(draftLayout);
  }

  function handleLayoutChange(next: WidgetLayout[]) {
    if (mode !== 'edit') return;
    setDraftLayout(next);
  }

  function handleRemove(widgetId: string) {
    if (mode !== 'edit') return;
    setDraftLayout((prev) =>
      (prev ?? savedLayout).filter((w) => w.widgetId !== widgetId),
    );
  }

  function handleAdd(widgetId: WidgetId) {
    setDraftLayout((prev) => {
      const base = prev ?? savedLayout;
      if (base.some((w) => w.widgetId === widgetId)) return base;
      const spot = findFreeSpot(base);
      return [...base, { widgetId, x: spot.x, y: spot.y, w: 1, h: 1 }];
    });
  }

  // Atalhos: Esc cancela, Ctrl/Cmd+S salva — apenas em edit mode.
  useEffect(() => {
    if (mode !== 'edit') return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      } else if (
        (e.key === 's' || e.key === 'S') &&
        (e.ctrlKey || e.metaKey)
      ) {
        e.preventDefault();
        saveEdit();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, draftLayout]);

  const greeting = getGreeting(new Date(), user?.name);
  const presentIds = activeLayout.map((w) => w.widgetId);

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <h1
          data-tour="dashboard-greeting"
          className="font-display text-2xl tracking-tight text-[var(--fg)] md:text-3xl"
        >
          {greeting}
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          <div data-tour="dashboard-period" className="flex flex-wrap items-center gap-2">
            <SegmentedControl
              ariaLabel="Período"
              items={PERIODS}
              value={period}
              onChange={setPeriod}
            />
            <SegmentedControl
              ariaLabel="Escopo"
              items={SCOPES}
              value={scope}
              onChange={setScope}
            />
          </div>
          <button
            type="button"
            data-tour="dashboard-edit-layout"
            onClick={() => (mode === 'edit' ? cancelEdit() : enterEditMode())}
            aria-label={
              mode === 'edit' ? 'Sair do modo edição' : 'Editar layout'
            }
            title={mode === 'edit' ? 'Sair do modo edição' : 'Editar layout'}
            className={cn(
              'inline-flex h-8 w-8 items-center justify-center rounded-md border transition',
              'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]',
              mode === 'edit' && 'border-[var(--accent)]/60 text-[var(--accent)]',
            )}
          >
            {mode === 'edit' ? (
              <X className="h-4 w-4" />
            ) : (
              <LayoutGrid className="h-4 w-4" />
            )}
          </button>
        </div>
      </header>

      {mode === 'edit' && (
        <div
          className={cn(
            'flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2',
            'border-[var(--accent)]/40 bg-[var(--accent)]/5',
          )}
        >
          <p className="text-xs text-[var(--fg-muted)]">
            Modo edição: arraste para mover, alças para redimensionar.
            <span className="ml-2 opacity-60">Esc cancela · Ctrl+S salva</span>
          </p>
          <div className="flex items-center gap-2">
            <AddWidgetMenu
              pageId="home"
              presentIds={presentIds}
              onAdd={(id) => handleAdd(id as WidgetId)}
            />
            <button
              type="button"
              onClick={cancelEdit}
              className={cn(
                'rounded-md border px-3 py-1.5 text-xs font-medium transition',
                'border-[var(--border)] bg-[var(--surface)] text-[var(--fg-muted)] hover:text-[var(--fg)]',
              )}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={saveEdit}
              disabled={saveMutation.isPending}
              className={cn(
                'rounded-md px-3 py-1.5 text-xs font-medium transition',
                'bg-[var(--accent)] text-[var(--accent-fg)] hover:opacity-90',
                saveMutation.isPending && 'opacity-60',
              )}
            >
              {saveMutation.isPending ? 'Salvando...' : 'Salvar'}
            </button>
          </div>
        </div>
      )}

      {saveMutation.isError && (
        <p className="text-xs text-red-500">
          Falha ao salvar o layout. Tente novamente.
        </p>
      )}

      <div data-tour="dashboard-widgets">
        <DashboardGrid
          pageId="home"
          layout={activeLayout}
          mode={mode}
          onLayoutChange={handleLayoutChange}
          onRemove={handleRemove}
        />
      </div>
    </div>
  );
}
